import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  useTheme,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ThemedDateTimePicker from "./ThemedDateTimePicker";
import API_BASE_URL from "../apiConfig";
import { calculateFee } from "../utils/feeCalculator";
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  TRANSPARENT_PINK_05,
  TRANSPARENT_PINK_08,
  TRANSPARENT_PINK_2,
  TRANSPARENT_BLACK_08,
} from "../constants";

const getDefaultForm = () => ({
  title: "",
  description: "",
  start_time: dayjs(),
  end_time: dayjs().add(2, "hour"),
  location: "",
  location_url: "",
  location_display_name: "",
  location_is_url: false,
  registration_url: "",
  startgg_event: false,
  slug: "",
  embed_registration: false,
  iframe_data: "",
  ticket_price: "",
  ticket_currency: "USD",
  ticket_available: false,
  ticket_quantity: "",
  ticket_description: "",
  rsvp_enabled: false,
  rsvp_limit: "",
  rsvp_deadline: null,
  private_event: false,
  hide_location_until_rsvp: false,
  hide_rsvp_list: false,
  chess_event: false,
  dnd_event: false,
});

function EventModal({ open, onClose, onEventCreated, clubId, clubName }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [form, setForm] = useState(getDefaultForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [copiedFlyerUrl, setCopiedFlyerUrl] = useState(null);
  const [previousEvents, setPreviousEvents] = useState([]);
  const [previousEventsLoading, setPreviousEventsLoading] = useState(false);
  const [copyFromEventId, setCopyFromEventId] = useState("");
  const [copyingEvent, setCopyingEvent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !clubId) return;

    const fetchPreviousEvents = async () => {
      setPreviousEventsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/events/all`, {
          credentials: "include",
        });
        if (response.ok) {
          const events = await response.json();
          setPreviousEvents(Array.isArray(events) ? events : []);
        } else {
          setPreviousEvents([]);
        }
      } catch (err) {
        console.error("Error fetching previous events:", err);
        setPreviousEvents([]);
      } finally {
        setPreviousEventsLoading(false);
      }
    };

    fetchPreviousEvents();
  }, [open, clubId]);

  const populateFormFromEvent = (eventData) => {
    const hasLocationUrl = !!(eventData.location_url && eventData.location_display_name);
    const sourceStart = eventData.start_time ? dayjs(eventData.start_time) : null;
    const sourceEnd = eventData.end_time ? dayjs(eventData.end_time) : null;
    const durationMinutes =
      sourceStart && sourceEnd && sourceEnd.isAfter(sourceStart)
        ? sourceEnd.diff(sourceStart, "minute")
        : 120;
    const newStart = dayjs().add(1, "hour").startOf("hour");

    setForm({
      title: eventData.title || "",
      description: eventData.description || "",
      start_time: newStart,
      end_time: newStart.add(durationMinutes, "minute"),
      location: hasLocationUrl ? "" : eventData.location || "",
      location_url: eventData.location_url || "",
      location_display_name: eventData.location_display_name || "",
      location_is_url: hasLocationUrl,
      registration_url: eventData.registration_url || "",
      startgg_event: eventData.startgg_event || false,
      slug: eventData.slug || "",
      embed_registration: eventData.embed_registration || false,
      iframe_data: eventData.iframe_data || "",
      ticket_price: eventData.ticket_price ?? "",
      ticket_currency: eventData.ticket_currency || "USD",
      ticket_available: eventData.ticket_available || false,
      ticket_quantity: eventData.ticket_quantity ?? "",
      ticket_description: eventData.ticket_description || "",
      rsvp_enabled: eventData.rsvp_enabled || false,
      rsvp_limit: eventData.rsvp_limit ?? "",
      rsvp_deadline: null,
      private_event: eventData.private_event || false,
      hide_location_until_rsvp: eventData.hide_location_until_rsvp || false,
      hide_rsvp_list: eventData.hide_rsvp_list || false,
      chess_event: eventData.chess_event || false,
      dnd_event: eventData.dnd_event || false,
    });

    setSelectedFile(null);
    setFilePreview(null);
    setCopiedFlyerUrl(eventData.flyer_url || null);
  };

  const handleCopyFromEvent = async (eventId) => {
    setCopyFromEventId(eventId);
    if (!eventId) {
      setForm(getDefaultForm());
      setSelectedFile(null);
      setFilePreview(null);
      setCopiedFlyerUrl(null);
      return;
    }

    setCopyingEvent(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to load event details");
      }
      const eventData = await response.json();
      populateFormFromEvent(eventData);
    } catch (err) {
      console.error("Error loading event to copy:", err);
      setError(err.message || "Failed to load event details");
      setCopyFromEventId("");
    } finally {
      setCopyingEvent(false);
    }
  };

  const formatEventOptionLabel = (event) => {
    const date = event.start_time
      ? dayjs(event.start_time).format("MMM D, YYYY")
      : "No date";
    return `${event.title} (${date})`;
  };


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (field) => (event) => {
    const checked = event.target.checked;
    setForm((prev) => {
      const next = { ...prev, [field]: checked };

      if (field === "chess_event" && checked) {
        next.dnd_event = false;
      }
      if (field === "dnd_event" && checked) {
        next.chess_event = false;
      }

      const specialEvent = next.chess_event || next.dnd_event;

      // If enabling RSVP, disable ticket sales (unless chess or DnD event is enabled)
      if (field === "rsvp_enabled" && checked && !specialEvent) {
        next.ticket_available = false;
      }

      // If enabling ticket sales, disable RSVP, private event, and location hiding (unless chess or DnD event is enabled)
      if (field === "ticket_available" && checked && !specialEvent) {
        next.rsvp_enabled = false;
        next.private_event = false;
        next.hide_location_until_rsvp = false;
      }

      return next;
    });
  };

  const handleDateChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setCopiedFlyerUrl(null);
      setError("");

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setCopiedFlyerUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    
    if (!form.description.trim()) {
      setError("Description is required");
      return;
    }
    
    if (form.location_is_url) {
      if (!form.location_display_name.trim()) {
        setError("Location display name is required");
        return;
      }
      if (!form.location_url.trim()) {
        setError("Location URL is required");
        return;
      }
      // Basic URL validation
      try {
        new URL(form.location_url.trim());
      } catch {
        setError("Location URL must be a valid URL");
        return;
      }
    } else {
      if (!form.location.trim()) {
        setError("Location is required");
        return;
      }
    }
    
    if (!form.start_time) {
      setError("Start time is required");
      return;
    }
    
    if (!form.end_time) {
      setError("End time is required");
      return;
    }
    
    if (form.end_time <= form.start_time) {
      setError("End time must be after start time");
      return;
    }

    // Validate Start.gg specific fields
    if (form.startgg_event && !form.slug.trim()) {
      setError("Slug is required when Start.gg event is enabled");
      return;
    }

    if (form.embed_registration && !form.iframe_data.trim()) {
      setError("Iframe data is required when embed registration is enabled");
      return;
    }

    // Validate RSVP fields
    if (form.rsvp_enabled && form.rsvp_limit && parseInt(form.rsvp_limit) <= 0) {
      setError("RSVP limit must be greater than 0");
      return;
    }

    if (form.rsvp_enabled && form.rsvp_deadline && form.rsvp_deadline <= new Date()) {
      setError("RSVP deadline must be in the future");
      return;
    }

    setLoading(true);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('title', form.title.trim());
      formData.append('description', form.description.trim());
      formData.append('start_time', form.start_time.toISOString());
      formData.append('end_time', form.end_time.toISOString());
      if (form.location_is_url) {
        formData.append('location', form.location_display_name.trim());
        formData.append('location_url', form.location_url.trim());
        formData.append('location_display_name', form.location_display_name.trim());
      } else {
        formData.append('location', form.location.trim());
        formData.append('location_url', '');
        formData.append('location_display_name', '');
      }
      formData.append('registration_url', form.registration_url.trim());
      formData.append('startgg_event', form.startgg_event);
      formData.append('slug', form.slug.trim());
      formData.append('embed_registration', form.embed_registration);
      formData.append('iframe_data', form.iframe_data.trim());
      formData.append('ticket_price', form.ticket_price);
      formData.append('ticket_currency', form.ticket_currency);
      formData.append('ticket_available', form.ticket_available);
      formData.append('ticket_quantity', form.ticket_quantity);
      formData.append('ticket_description', form.ticket_description.trim());
      formData.append('rsvp_enabled', form.rsvp_enabled);
      formData.append('rsvp_limit', form.rsvp_limit);
      if (form.rsvp_deadline) {
        formData.append('rsvp_deadline', form.rsvp_deadline.toISOString());
      }
      formData.append('private_event', form.private_event);
      formData.append('hide_location_until_rsvp', form.hide_location_until_rsvp);
      formData.append('hide_rsvp_list', form.hide_rsvp_list);
      formData.append('chess_event', form.chess_event);
      formData.append('dnd_event', form.dnd_event);
      
      if (selectedFile) {
        formData.append("flyer", selectedFile);
      } else if (copiedFlyerUrl) {
        formData.append("flyer_url", copiedFlyerUrl);
      }

      const response = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/events`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.error || "Failed to create event");
      }

      const newEvent = await response.json();
      onEventCreated(newEvent);
      handleClose();
      
      // Redirect to the event landing page
      navigate(`/events/${newEvent.id}`);
    } catch (error) {
      console.error("Create event error:", error);
      setError(error.message || "Failed to create event");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(getDefaultForm());
    setSelectedFile(null);
    setFilePreview(null);
    setCopiedFlyerUrl(null);
    setCopyFromEventId("");
    setError("");
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          Create New Event
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {clubName}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={previousEventsLoading || copyingEvent || loading}>
            <InputLabel id="copy-from-event-label">
              Copy from Previous Event (Optional)
            </InputLabel>
            <Select
              labelId="copy-from-event-label"
              id="copy_from_event"
              value={copyFromEventId}
              label="Copy from Previous Event (Optional)"
              onChange={(e) => handleCopyFromEvent(e.target.value)}
              sx={{
                fontSize: 18,
                height: 56,
              }}
            >
              <MenuItem value="">
                <em>Start from scratch</em>
              </MenuItem>
              {previousEvents.map((event) => (
                <MenuItem key={event.id} value={event.id} sx={{ fontSize: 18, minHeight: 48 }}>
                  {formatEventOptionLabel(event)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {copyingEvent
                ? "Loading event details..."
                : "Select a past event to pre-fill this form"}
            </FormHelperText>
          </FormControl>

          {copyingEvent && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <TextField
            margin="dense"
            required
            fullWidth
            id="title"
            label="Event Title"
            name="title"
            autoFocus
            value={form.title}
            onChange={handleChange}
            placeholder="Enter event title"
            sx={{ mb: 1 }}
          />
          
          <TextField
            margin="dense"
            required
            fullWidth
            id="description"
            label="Description"
            name="description"
            multiline
            rows={3}
            value={form.description}
            onChange={handleChange}
            placeholder="Describe your event"
            sx={{ mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={form.location_is_url}
                onChange={handleSwitchChange('location_is_url')}
                color="primary"
              />
            }
            label="Location is a URL"
            sx={{ mb: 2 }}
          />
          
          {form.location_is_url ? (
            <>
              <TextField
                margin="dense"
                required
                fullWidth
                id="location_display_name"
                label="Location Display Name"
                name="location_display_name"
                value={form.location_display_name}
                onChange={handleChange}
                placeholder="e.g., Tournament Venue, Online Event"
                sx={{ mb: 1 }}
              />
              <TextField
                margin="dense"
                required
                fullWidth
                id="location_url"
                label="Location URL"
                name="location_url"
                value={form.location_url}
                onChange={handleChange}
                placeholder="https://example.com/venue"
                sx={{ mb: 2 }}
              />
            </>
          ) : (
            <TextField
              margin="dense"
              required
              fullWidth
              id="location"
              label="Location"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Where is the event taking place?"
              sx={{ mb: 2 }}
            />
          )}
          
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <ThemedDateTimePicker
              label="Start Time"
              value={form.start_time}
              onChange={(value) => handleDateChange("start_time", value)}
              views={['year', 'month', 'day', 'hours', 'minutes']}
              minDateTime={dayjs()}
              required
            />
            <ThemedDateTimePicker
              label="End Time"
              value={form.end_time}
              onChange={(value) => handleDateChange("end_time", value)}
              views={['year', 'month', 'day', 'hours', 'minutes']}
              minDateTime={form.start_time || dayjs()}
              required
            />
          </Box>

          {/* File Upload Section */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              Event Flyer (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Upload an image for your event flyer. Supported formats: JPEG, PNG, GIF, WebP (max 5MB)
            </Typography>
            
            {!selectedFile && !copiedFlyerUrl ? (
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  border: '2px dashed #ccc',
                  borderColor: PRIMARY_PINK,
                  color: PRIMARY_PINK,
                  '&:hover': {
                    borderColor: PRIMARY_PINK_DARK,
                    backgroundColor: TRANSPARENT_PINK_05,
                    color: PRIMARY_PINK_DARK
                  }
                }}
              >
                Choose Flyer Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src={filePreview || copiedFlyerUrl}
                  alt="Flyer preview"
                  sx={{
                    width: 100,
                    height: 100,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid #ccc'
                  }}
                />
                <Box>
                  {selectedFile ? (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        Flyer copied from previous event
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                        {copiedFlyerUrl}
                      </Typography>
                    </>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={handleRemoveFile}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* RSVP Section */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              RSVP Settings (Free Events)
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable RSVP to allow users to sign up for your free event. Each user can RSVP for 1 spot.
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.rsvp_enabled}
                  onChange={handleSwitchChange("rsvp_enabled")}
                  name="rsvp_enabled"
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.rsvp_enabled ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.rsvp_enabled ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
              }
              label="Enable RSVP for Free Event"
              sx={{ mb: 1 }}
            />

            {form.rsvp_enabled && (
              <Box sx={{ ml: 2, mb: 1 }}>
                <TextField
                  margin="dense"
                  fullWidth
                  id="rsvp_limit"
                  label="RSVP Limit (Optional)"
                  name="rsvp_limit"
                  type="number"
                  value={form.rsvp_limit}
                  onChange={handleChange}
                  placeholder="Leave empty for unlimited"
                  inputProps={{ min: 1 }}
                  helperText="Maximum number of people who can RSVP"
                  sx={{ mb: 1 }}
                />

                <ThemedDateTimePicker
                  label="RSVP Deadline (Optional)"
                  value={form.rsvp_deadline}
                  onChange={(value) => handleDateChange("rsvp_deadline", value)}
                  views={['year', 'month', 'day', 'hours', 'minutes']}
                  minDateTime={dayjs()}
                />
              </Box>
            )}
          </Box>

          {/* Ticket Sales Section */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              Ticket Sales
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.ticket_available}
                  onChange={handleSwitchChange("ticket_available")}
                  name="ticket_available"
                  disabled={form.rsvp_enabled && !form.chess_event && !form.dnd_event}
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.ticket_available ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.ticket_available ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
              }
              label="Enable Ticket Sales"
              sx={{ mb: 1 }}
            />

            {form.ticket_available && (
              <Box sx={{ ml: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    id="ticket_price"
                    label="Ticket Price"
                    name="ticket_price"
                    type="number"
                    value={form.ticket_price}
                    onChange={handleChange}
                    placeholder="0.00"
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    margin="dense"
                    fullWidth
                    id="ticket_currency"
                    label="Currency"
                    name="ticket_currency"
                    value={form.ticket_currency}
                    onChange={handleChange}
                    select
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="CAD">CAD</MenuItem>
                  </TextField>
                </Box>

                {/* Fee Calculation Display */}
                {form.ticket_price && parseFloat(form.ticket_price) > 0 && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: TRANSPARENT_PINK_05, borderRadius: 1, border: `1px solid ${TRANSPARENT_PINK_2}` }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: PRIMARY_PINK, mb: 1 }}>
                      💰 Fee Breakdown
                    </Typography>
                    {(() => {
                      const fee = calculateFee(parseFloat(form.ticket_price));
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            <strong>Platform Fee:</strong> ${fee.totalFee.toFixed(2)} (8%)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            <strong>Club Receives:</strong> ${fee.clubReceives.toFixed(2)} (92%)
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                <TextField
                  margin="dense"
                  fullWidth
                  id="ticket_quantity"
                  label="Available Tickets"
                  name="ticket_quantity"
                  type="number"
                  value={form.ticket_quantity}
                  onChange={handleChange}
                  placeholder="Leave empty for unlimited"
                  inputProps={{ min: 1 }}
                  sx={{ mb: 1 }}
                />

                <TextField
                  margin="dense"
                  fullWidth
                  id="ticket_description"
                  label="Ticket Description"
                  name="ticket_description"
                  multiline
                  rows={2}
                  value={form.ticket_description}
                  onChange={handleChange}
                  placeholder="What's included with this ticket?"
                  sx={{ mb: 1 }}
                />
              </Box>
            )}
          </Box>

          {/* Privacy & Visibility Section */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              Privacy & Visibility Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.private_event}
                  onChange={handleSwitchChange("private_event")}
                  name="private_event"
                  disabled={form.ticket_available}
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.private_event ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.private_event ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
              }
              label="Private Event (Users must apply to join)"
              sx={{ mb: 1 }}
            />
            
            {form.ticket_available && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2, fontStyle: 'italic' }}>
                Private events are not available for ticketed events. Users need to see event details to purchase tickets.
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={form.hide_location_until_rsvp}
                  onChange={handleSwitchChange("hide_location_until_rsvp")}
                  name="hide_location_until_rsvp"
                  disabled={form.ticket_available}
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.hide_location_until_rsvp ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.hide_location_until_rsvp ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
              }
              label="Hide Event Location Until RSVP'd"
              sx={{ mb: 1 }}
            />
            
            {form.ticket_available && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2, fontStyle: 'italic' }}>
                Location hiding is not available for ticketed events. Users need to see the location to purchase tickets.
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={form.hide_rsvp_list}
                  onChange={handleSwitchChange("hide_rsvp_list")}
                  name="hide_rsvp_list"
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.hide_rsvp_list ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.hide_rsvp_list ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
              }
              label="Hide RSVP List from Public View"
              sx={{ mb: 1 }}
            />
          </Box>

          {/* Registration Section */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              Registration Settings
            </Typography>
            
            <TextField
              margin="dense"
              fullWidth
              id="registration_url"
              label="Registration Link"
              name="registration_url"
              value={form.registration_url}
              onChange={handleChange}
              placeholder="https://example.com/register"
              sx={{ mb: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.startgg_event}
                  onChange={handleSwitchChange("startgg_event")}
                  name="startgg_event"
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.startgg_event ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.startgg_event ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
              }
              label="Start.gg Event"
              sx={{ mb: 1 }}
            />

            {form.startgg_event && (
              <Box sx={{ ml: 2, mb: 1 }}>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="slug"
                  label="Slug"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder="tournament/tournament-name/event/event-name"
                  helperText="Format: tournament/tournament-name/event/event-name (from start.gg URL)"
                  sx={{ mb: 1 }}
                />

                <FormControlLabel
                  control={
                                    <Switch
                  checked={form.embed_registration}
                  onChange={handleSwitchChange("embed_registration")}
                  name="embed_registration"
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.embed_registration ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.embed_registration ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        color: '#ccc !important',
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: '#ccc !important',
                      },
                    },
                  }}
                />
                  }
                  label="Embed Registration"
                  sx={{ mb: 1 }}
                />

                {form.embed_registration && (
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    id="iframe_data"
                    label="Iframe Data"
                    name="iframe_data"
                    multiline
                    rows={3}
                    value={form.iframe_data}
                    onChange={handleChange}
                    placeholder="<iframe src='...'></iframe>"
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Chess Event Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
              Chess Event Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.chess_event}
                  onChange={handleSwitchChange("chess_event")}
                  name="chess_event"
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.chess_event ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.chess_event ? PRIMARY_PINK : '#ccc',
                      },
                    },
                  }}
                />
              }
              label="Chess Event"
              sx={{ mb: 1 }}
            />
            
            {form.chess_event && (
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Chess events require players to fill out a registration form with their chess information.
                </Typography>
                {form.rsvp_enabled && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    For RSVP events: Players will be prompted to fill out the chess registration form when they RSVP.
                  </Typography>
                )}
                {form.ticket_available && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    For ticketed events: Players must complete the chess registration form before purchasing tickets.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* DnD Event Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
              DnD Event Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.dnd_event}
                  onChange={handleSwitchChange("dnd_event")}
                  name="dnd_event"
                  sx={{
                    '& .MuiSwitch-root': {
                      '& .MuiSwitch-switchBase': {
                        color: form.dnd_event ? PRIMARY_PINK : '#ccc',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: TRANSPARENT_PINK_08,
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PRIMARY_PINK,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: form.dnd_event ? PRIMARY_PINK : '#ccc',
                      },
                    },
                  }}
                />
              }
              label="DnD Event"
              sx={{ mb: 1 }}
            />
            
            {form.dnd_event && (
              <Box sx={{ ml: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  DnD events require players to fill out a registration form with their campaign and class preferences.
                </Typography>
                {form.rsvp_enabled && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    For RSVP events: Players will be prompted to fill out the DnD registration form when they RSVP.
                  </Typography>
                )}
                {form.ticket_available && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    For ticketed events: Players must complete the DnD registration form before purchasing tickets.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          disabled={loading}
          sx={{
            color: "#000000",
            borderColor: "#000000",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#000000",
              backgroundColor: TRANSPARENT_BLACK_08
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: "#000000",
            color: "#FFFFFF",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              bgcolor: "#333333"
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Create Event"}
        </Button>
      </DialogActions>
    </Dialog>
    </LocalizationProvider>
  );
}

export default EventModal; 